import React, {
	Component,
	type ReactElement,
	// eslint-disable-next-line jira/restricted/react-component-props
	type ComponentProps,
	type SyntheticEvent,
	type MouseEvent,
} from 'react';
import { styled } from '@compiled/react';
import noop from 'lodash/noop';
import type InlineMessage from '@atlaskit/inline-message';
import { colors } from '@atlaskit/theme';
import { token } from '@atlaskit/tokens';
import AKTooltip from '@atlaskit/tooltip';
import AsyncIcon from '@atlassian/jira-common-components-async-icon/src/view.tsx';
import { statusCategories } from '@atlassian/jira-common-constants/src/index.tsx';
import { injectIntl, defineMessages, type IntlShape } from '@atlassian/jira-intl';
import { AsyncIssueAssigneeField } from '@atlassian/jira-issue-field-assignee/src/ui/popover/async.tsx';
import { useFieldValue } from '@atlassian/jira-issue-field-base/src/services/field-value-service/index.tsx';
import { ASSIGNEE_TYPE } from '@atlassian/jira-platform-field-config/src/index.tsx';
import { toIssueKey, type IssueKey } from '@atlassian/jira-shared-types/src/general.tsx';

const messages = defineMessages({
	issueTypeTitle: {
		id: 'servicedesk-issue-line-card-group.common.utils.issue-line-card.issue-line-card-view.issue-type-title',
		defaultMessage: 'Issue type: {issueTypeName}',
		description: 'A text title for the issue type icon, mainly for accessibility purposes.',
	},
	issuePriorityTitle: {
		id: 'servicedesk-issue-line-card-group.common.utils.issue-line-card.issue-line-card-view.issue-priority-title',
		defaultMessage: 'Priority: {issuePriorityName}',
		description: 'A text title for the issue priority icon, mainly for accessibility purposes.',
	},
});

const { DONE } = statusCategories;

const priorityIconSize = 20;

type Props = {
	issueSummary: string;
	issueTypeIconUrl: string;
	issueTypeName?: string;
	issueKey?: IssueKey;
	issueLink?: string;
	issuePriorityUrl?: string;
	issuePriorityName?: string;
	isHovering?: boolean;
	isActive?: boolean;
	isFocused?: boolean;
	hasError?: boolean;
	errorInlineMessage?: ReactElement<ComponentProps<typeof InlineMessage>>;
	statusCategory?: string;
	onDelete: (issueKey: IssueKey, globalId?: string) => void;
	intl: IntlShape;
};

// DO NOT MODIFY THIS. TODO FLY-448 - consider changing this to use @atlassian/jira-issue-line-card instead
// eslint-disable-next-line jira/react/no-class-components
export class IssueLineCard extends Component<Props> {
	static defaultProps = {
		isHovering: false,
		isActive: false,
		isFocused: false,
		hasError: false,
		onDelete: noop,
	};

	shouldRenderKey() {
		return !!this.props.issueKey;
	}

	isActionVisible() {
		return (
			this.props.isActive !== true &&
			(this.props.isHovering === true || this.props.isFocused === true)
		);
	}

	deleteButtonAction(e: SyntheticEvent<HTMLElement>) {
		e.stopPropagation();
		if (this.props.issueKey) {
			this.props.onDelete(this.props.issueKey);
		}
	}

	handleErrorDialogClick = (e: MouseEvent<HTMLElement>) => {
		e.stopPropagation();
	};

	handleErrorDialogMouseDown = (e: MouseEvent<HTMLElement>) => {
		e.preventDefault();
		e.stopPropagation();
	};

	handleLinkKeyClick = (e: MouseEvent<HTMLAnchorElement>) => {
		e.preventDefault();
		if (
			this.props.issueLink !== undefined &&
			this.props.issueLink !== null &&
			this.props.issueLink.length
		) {
			// eslint-disable-next-line jira/jira-ssr/no-unchecked-globals-usage
			window.open(this.props.issueLink, '_blank');
		}
	};

	renderAssigneeInteractive() {
		const { issueKey } = this.props;

		if (issueKey === undefined) {
			return null;
		}

		return (
			<PaddedFlexContainer
				role="button"
				onClick={(e: React.MouseEvent<HTMLDivElement>) => e.preventDefault()}
				onFocus={(e: React.FocusEvent<HTMLDivElement, Element>) => e.stopPropagation()}
				onKeyDown={(e: React.KeyboardEvent<HTMLDivElement>) =>
					(e.key === 'Enter' || e.key === 'Space') && e.stopPropagation()
				}
				tabIndex={0}
			>
				<AsyncIssueAssigneeField issueKey={toIssueKey(issueKey)} />
			</PaddedFlexContainer>
		);
	}

	renderPriority() {
		const { intl, issuePriorityUrl, issuePriorityName } = this.props;

		if (issuePriorityUrl === null || issuePriorityUrl === undefined || !issuePriorityUrl.length) {
			return null;
		}

		const title = intl.formatMessage(messages.issuePriorityTitle, { issuePriorityName });

		return (
			<AKTooltip content={issuePriorityName || ''} position="bottom">
				<AsyncIcon
					alt={title}
					url={issuePriorityUrl}
					width={priorityIconSize}
					height={priorityIconSize}
				/>
			</AKTooltip>
		);
	}

	renderError() {
		if (this.props.hasError !== true && !this.props.errorInlineMessage) {
			return null;
		}
		return (
			<PaddedFlexContainer
				onClick={this.handleErrorDialogClick}
				onMouseDown={this.handleErrorDialogMouseDown}
				role="button"
				tabIndex={0}
				onKeyDown={(e: React.KeyboardEvent<HTMLDivElement>) =>
					(e.key === 'Enter' || e.key === 'Space') && this.handleErrorDialogClick
				}
			>
				{this.props.errorInlineMessage}
			</PaddedFlexContainer>
		);
	}

	renderSummary() {
		const { isActive, issueSummary } = this.props;
		return (
			<Summary
				// eslint-disable-next-line jira/integration/test-id-by-folder-structure
				data-testid="issue.issue-view.views.common.issue-line-card.issue-line-card-view.summary"
				// @ts-expect-error - TS2322 - Type 'boolean | undefined' is not assignable to type 'boolean'.
				isActive={isActive}
			>
				<AKTooltip content={issueSummary} position="bottom">
					<SummaryInner>{issueSummary}</SummaryInner>
				</AKTooltip>
			</Summary>
		);
	}

	renderKey() {
		return (
			<Key
				// eslint-disable-next-line jira/integration/test-id-by-folder-structure
				data-testid="issue.issue-view.views.common.issue-line-card.issue-line-card-view.key"
				href={this.props.issueLink}
				isActive={this.props.isActive}
				isDone={this.props.statusCategory === DONE}
				onClick={this.handleLinkKeyClick}
			>
				{this.props.issueKey}
			</Key>
		);
	}

	renderType() {
		const { intl, issueTypeIconUrl, issueTypeName } = this.props;

		if (issueTypeName === null || issueTypeName === undefined || !issueTypeName.length) {
			return null;
		}

		const title = intl.formatMessage(messages.issueTypeTitle, { issueTypeName });

		// Using an intermediate div since AKTooltip needs an element to bind to
		// We aren't wrapping just ListIcon or AsyncIcon since AKTooltip is stateful
		// and we don't want it to unmount as the mouseHover changes
		return (
			<AKTooltip content={issueTypeName} position="bottom">
				<>
					<FlexContainer>
						<AsyncIcon alt={title} url={issueTypeIconUrl} />
					</FlexContainer>
				</>
			</AKTooltip>
		);
	}

	render() {
		return (
			<Card>
				{this.renderType()}
				{this.shouldRenderKey() && this.renderKey()}
				{this.renderSummary()}
				{this.renderPriority()}
				<ConnectedIssueAssigneeContainer>
					{this.renderAssigneeInteractive()}
				</ConnectedIssueAssigneeContainer>
				{this.renderError()}
			</Card>
		);
	}
}

// DO NOT MODIFY THIS. TODO FLY-448 - consider changing this to use @atlassian/jira-issue-line-card instead

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const IssueLineCardWithHooks = (props: any) => {
	// tipying this causes a ripple effect everywhere else
	const { issueKey = toIssueKey('') } = props;
	const [assignee] = useFieldValue({ issueKey, fieldKey: ASSIGNEE_TYPE });
	const shouldShowInteractiveAssignee = assignee !== undefined;
	return <IssueLineCard shouldShowInteractiveAssignee={shouldShowInteractiveAssignee} {...props} />;
};

// eslint-disable-next-line jira/js/no-inject-intl-if-functional-component
export default injectIntl(IssueLineCardWithHooks);

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled
const FlexContainer = styled.div({
	display: 'flex',
	alignItems: 'center',
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const ConnectedIssueAssigneeContainer = styled.div({
	pointerEvents: 'none',
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const Card = styled(FlexContainer)({
	width: '100%',
	height: '100%',
	position: 'relative',
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const PaddedFlexContainer = styled(FlexContainer)({
	paddingLeft: token('space.100'),
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled, @atlaskit/design-system/no-html-anchor -- To migrate as part of go/ui-styling-standard
const Key = styled.a<{ isActive?: boolean; isDone?: boolean }>({
	outline: 'none',
	marginLeft: token('space.100'),
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-dynamic-styles -- Ignored via go/DSP-18766
	color: (props) =>
		props.isActive
			? // eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values -- Ignored via go/DSP-18766
				token('color.text.selected', colors.B400)
			: // eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values -- Ignored via go/DSP-18766
				token('color.text.subtlest', colors.N90),
	font: token('font.body.UNSAFE_small'),
	fontWeight: token('font.weight.semibold'),
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-dynamic-styles -- Ignored via go/DSP-18766
	textDecoration: (props) => (props.isDone ? 'line-through' : 'none'),
	whiteSpace: 'nowrap',
	'&:hover, &:active, &:visited, &:link': {
		outline: 'none',
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-dynamic-styles -- Ignored via go/DSP-18766
		color: (props) =>
			props.isActive
				? // eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values -- Ignored via go/DSP-18766
					token('color.text.selected', colors.B400)
				: // eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values -- Ignored via go/DSP-18766
					token('color.text.subtlest', colors.N90),
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-dynamic-styles -- Ignored via go/DSP-18766
		textDecoration: (props) => (props.isDone ? 'line-through' : 'none'),
	},
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const Summary = styled.div<{ isActive: boolean }>({
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values, @atlaskit/ui-styling-standard/no-dynamic-styles -- Ignored via go/DSP-18766
	color: (props) => (props.isActive ? token('color.text.selected', colors.B400) : ''),
	paddingLeft: token('space.100'),
	flex: 1,
	whiteSpace: 'nowrap',
	overflow: 'hidden',
	textOverflow: 'ellipsis',
});

// eslint-disable-next-line  @atlaskit/ui-styling-standard/no-styled, @atlaskit/ui-styling-standard/no-exported-styles -- Ignored via go/DSP-18766
export const SummaryInner = styled.div({
	overflow: 'hidden',
	textOverflow: 'ellipsis',
	whiteSpace: 'nowrap',
});
